import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import * as Dialog from '@radix-ui/react-dialog'
import { X, Check, Zap } from 'lucide-react'

import { cn } from '@/lib/utils'
import { api } from '@/lib/api'
import { useUpgradeStore } from '@/stores/upgrade.store'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CreateSubscriptionResponse {
  id: string
  status: string
}

type BillingCycle = 'monthly' | 'yearly'
type PlanKey = 'pro' | 'max'

// ---------------------------------------------------------------------------
// Plan config
// ---------------------------------------------------------------------------

const PLANS: Record<
  PlanKey,
  {
    label: string
    monthlyPrice: string
    yearlyPrice: string
    applications: string
    features: string[]
    monthlyEnvKey: string
    yearlyEnvKey: string
  }
> = {
  pro: {
    label: 'Pro',
    monthlyPrice: '₹1,500/mo',
    yearlyPrice: '₹10,000/yr',
    applications: '8',
    features: [
      '8 applications',
      'Unlimited recommenders',
      'Document storage',
      'Email reminders',
    ],
    monthlyEnvKey: 'VITE_RAZORPAY_PLAN_PRO_MONTHLY',
    yearlyEnvKey: 'VITE_RAZORPAY_PLAN_PRO_YEARLY',
  },
  max: {
    label: 'Max',
    monthlyPrice: '₹3,000/mo',
    yearlyPrice: '₹20,000/yr',
    applications: '20',
    features: [
      '20 applications',
      'Unlimited recommenders',
      'Document storage',
      'Priority support',
    ],
    monthlyEnvKey: 'VITE_RAZORPAY_PLAN_MAX_MONTHLY',
    yearlyEnvKey: 'VITE_RAZORPAY_PLAN_MAX_YEARLY',
  },
}

/**
 * Returns the Razorpay plan ID from import.meta.env for a given plan + cycle.
 */
function getPlanId(plan: PlanKey, cycle: BillingCycle): string {
  const envKey =
    cycle === 'monthly' ? PLANS[plan].monthlyEnvKey : PLANS[plan].yearlyEnvKey
  return (import.meta.env as Record<string, string>)[envKey] ?? ''
}

// ---------------------------------------------------------------------------
// UpgradeModal
// ---------------------------------------------------------------------------

/**
 * Global upgrade modal.
 *
 * Mount once in AppLayout. Controlled via the `useUpgradeStore` Zustand store.
 * Opens when:
 *  - `AppLimitError` is caught in the create-application mutation, or
 *  - Any "Upgrade" button calls `useUpgradeStore().open()`.
 */
export default function UpgradeModal() {
  const { isOpen, close } = useUpgradeStore()
  const queryClient = useQueryClient()

  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly')
  const [loading, setLoading] = useState<PlanKey | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function handleUpgrade(plan: PlanKey) {
    setError(null)
    setLoading(plan)

    const planId = getPlanId(plan, billingCycle)
    if (!planId) {
      setError('Payment configuration error. Please contact support.')
      setLoading(null)
      return
    }

    try {
      const res = await api.post<CreateSubscriptionResponse>(
        '/v1/billing/create-subscription',
        { planId, billingCycle },
      )

      const rzpKey = import.meta.env.VITE_RAZORPAY_KEY_ID as string | undefined
      if (!rzpKey) {
        setError('Payment configuration error. Please contact support.')
        setLoading(null)
        return
      }

      const rzp = new window.Razorpay({
        key: rzpKey,
        subscription_id: res.id,
        name: 'GradNomad',
        description: `${PLANS[plan].label} Plan`,
        handler: () => {
          queryClient.invalidateQueries({ queryKey: ['billing-status'] })
          close()
        },
      })

      rzp.open()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.')
    } finally {
      setLoading(null)
    }
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && close()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-xl border bg-background p-6 shadow-xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div>
              <Dialog.Title className="text-xl font-semibold text-foreground">
                Upgrade Your Plan
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-muted-foreground">
                Unlock more applications and features
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          {/* Billing cycle toggle */}
          <div className="mt-6 flex items-center justify-center gap-3">
            <span
              className={cn(
                'text-sm font-medium',
                billingCycle === 'monthly' ? 'text-foreground' : 'text-muted-foreground',
              )}
            >
              Monthly
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={billingCycle === 'yearly'}
              onClick={() =>
                setBillingCycle((c) => (c === 'monthly' ? 'yearly' : 'monthly'))
              }
              className={cn(
                'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                billingCycle === 'yearly' ? 'bg-brand-blue' : 'bg-muted',
              )}
            >
              <span
                className={cn(
                  'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                  billingCycle === 'yearly' ? 'translate-x-5' : 'translate-x-0',
                )}
              />
            </button>
            <span
              className={cn(
                'text-sm font-medium',
                billingCycle === 'yearly' ? 'text-foreground' : 'text-muted-foreground',
              )}
            >
              Yearly
              <span className="ml-1.5 rounded-full bg-brand-green/10 px-2 py-0.5 text-xs font-semibold text-brand-green">
                Save ~40%
              </span>
            </span>
          </div>

          {/* Plan cards */}
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {(Object.keys(PLANS) as PlanKey[]).map((plan) => {
              const p = PLANS[plan]
              const price = billingCycle === 'monthly' ? p.monthlyPrice : p.yearlyPrice
              const accentClass =
                plan === 'max'
                  ? 'border-brand-green ring-1 ring-brand-green'
                  : 'border-brand-purple ring-1 ring-brand-purple'

              return (
                <div
                  key={plan}
                  className={cn(
                    'flex flex-col rounded-lg border p-5 transition-shadow',
                    accentClass,
                  )}
                >
                  <div className="flex items-center gap-2">
                    <Zap
                      className={cn(
                        'h-4 w-4',
                        plan === 'max' ? 'text-brand-green' : 'text-brand-purple',
                      )}
                    />
                    <span className="font-semibold text-foreground">{p.label}</span>
                  </div>
                  <p
                    className={cn(
                      'mt-3 text-2xl font-bold',
                      plan === 'max' ? 'text-brand-green' : 'text-brand-purple',
                    )}
                  >
                    {price}
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {p.applications} applications
                  </p>
                  <ul className="mt-4 flex-1 space-y-2">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-foreground">
                        <Check className="h-4 w-4 flex-shrink-0 text-brand-green" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => handleUpgrade(plan)}
                    disabled={loading !== null}
                    className={cn(
                      'mt-5 w-full rounded-md px-4 py-2 text-sm font-semibold text-white transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60',
                      plan === 'max' ? 'bg-brand-green' : 'bg-brand-purple',
                    )}
                  >
                    {loading === plan
                      ? 'Processing...'
                      : `Upgrade to ${p.label}`}
                  </button>
                </div>
              )
            })}
          </div>

          {error && (
            <p className="mt-4 rounded-md bg-destructive/10 px-4 py-2 text-sm text-destructive">
              {error}
            </p>
          )}

          <p className="mt-4 text-center text-xs text-muted-foreground">
            Payments processed securely by Razorpay. Cancel anytime.
          </p>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
